/**
 * 등록형 폼 위젯 `risk-criteria-detail` (rc 공용 구현 — web 실폼·admin 캔버스 동형 등록).
 *
 * "평가기준 상세"를 구 SMS(RiskAssessmentOverviewStep의 평가기준 보기)와 동형으로 표시하는
 * **표시 전용** 위젯. 데이터 소스는 **자기 bindingKey 값 1개** — 기준표 합성 객체
 * `values[widget.bindingKey] = { likelihoodCriteria, severityCriteria, riskLevelCriteria,
 * levelCriteria }`(결정 2026-07-07-composer-template-variables 개정 5 — 선택 전파 모델):
 *  - likelihood+severity+riskLevel 전부 있음 → 매트릭스(교차 곱+등급색) + 관리기준 표 +
 *    빈도/강도 2단 목록 (FrequencySeverityDisplay)
 *  - 그 외 levelCriteria 있음 → 상중하 색 보더 카드 (ThreeLevelDisplay)
 *  - 전부 비면 안내 한 줄.
 * 방식별 표시 전환은 받은 데이터 형태의 내부 분기 — 별도 분기 축 불요.
 *
 * 값 공급: 초기 시드=prefillFrom(메타 defaultPrefillFrom: risk-assessment-config의 criteria),
 * 세트 전환=평가기준 세트 select의 writes(source:'criteria' → 이 위젯 bindingKey — 선택 옵션의
 * 원천 행 전파, applyDynamicSelectSelection). 재진입은 저장된 content 값 그대로(시드보다 우선).
 * 구 개정 4의 숨김 표 4키(ra-*-criteria) 파이프는 폐기.
 *
 * 매트릭스(교차 곱+등급색)는 범용 축 표현 밖 도메인 렌더지만, WYSIWYG(캔버스=배포본 동형)을 위해
 * 표시 컴포넌트와 함께 rc로 이동(개정 5 §4 — host 중복 구현 0 유지).
 */
import type { ReactNode } from 'react';
import {
  isLabelHidden,
  textStyleCss,
  WidgetLabelContent,
  COMPOSER_INPUT_LABEL_DEFAULT_STYLE,
  type ComposerRegisteredFormWidgetProps,
} from './composerWidgetTreeRender';
import {
  FrequencySeverityDisplay,
  ThreeLevelDisplay,
  type CriteriaLevelItem,
  type LevelCriteriaItem,
  type RiskLevelCriteriaItem,
} from './RiskCriteriaDisplay';

/** criteria 합성 객체의 필드 → 기준 항목 배열 (비배열/미시드는 빈 배열 degrade). */
function asItems<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export default function RiskCriteriaDetailWidget({
  widget,
  values,
}: ComposerRegisteredFormWidgetProps) {
  // 자기 bindingKey 값 1개(기준표 합성 객체) — 비객체(미시드·구형 content)는 빈 기준으로 degrade.
  const raw = values[widget.bindingKey];
  const criteria =
    raw !== null && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  const likelihood = asItems<CriteriaLevelItem>(criteria.likelihoodCriteria);
  const severity = asItems<CriteriaLevelItem>(criteria.severityCriteria);
  const riskLevels = asItems<RiskLevelCriteriaItem>(criteria.riskLevelCriteria);
  const levels = asItems<LevelCriteriaItem>(criteria.levelCriteria);

  // 라벨 규약 — 등록형 위젯 공통(labelStyle 숫자 모델 + labelHint, 표시 전용이라 * 없음 —
  // WidgetLabelContent showRequired:false). 내용 조립은 단일 정본(2026-08-24 수렴).
  // labelPlacement:'none'이면 라벨과 부속(labelHint)을 통째로 미렌더(일반 input 규약 동형).
  // 표시 전용 위젯이라 이름을 얹을 폼 컨트롤이 없어 aria-label 대체는 없다.
  const labelEl = widget.label && !isLabelHidden(widget) ? (
    <div
      className="mb-2"
      style={textStyleCss(widget.labelStyle, COMPOSER_INPUT_LABEL_DEFAULT_STYLE)}
    >
      {/* 라벨 프리미티브는 InputWidget을 직접 받는다(캐스트 없음). */}
      <WidgetLabelContent node={widget} values={values} showRequired={false} />
    </div>
  ) : null;

  const hasFrequencySeverity =
    likelihood.length > 0 && severity.length > 0 && riskLevels.length > 0;

  let body: ReactNode;
  if (hasFrequencySeverity) {
    body = (
      <FrequencySeverityDisplay
        config={{ likelihoodCriteria: likelihood, severityCriteria: severity, riskLevelCriteria: riskLevels }}
      />
    );
  } else if (levels.length > 0) {
    body = <ThreeLevelDisplay config={{ levelCriteria: levels }} />;
  } else {
    body = <p className="text-sm text-gray-400">표시할 평가기준 데이터가 없습니다.</p>;
  }

  return (
    <div>
      {labelEl}
      {body}
    </div>
  );
}
